import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { FileText, Download, Calendar, MapPin, Briefcase, GraduationCap, Languages, Wrench, Users } from 'lucide-react'

interface Application {
  id: string
  job_id: string | null
  job_title: string | null
  first_name: string
  last_name: string
  email: string
  phone: string
  city: string
  country: string | null
  education: Array<{
    _school: string
    _diploma: string
    _year: string
  }>
  languages: string[]
  additional_languages: string
  technical_skills: string[]
  additional_tech_skills: string
  tools: string[]
  additional_tools: string
  soft_skills: string[]
  additional_soft_skills: string
  references: Array<{
    _first_name: string
    _last_name: string
    _email: string
    _company: string
    _phone: string
  }>
  experiences: Array<{
    _company: string
    _role: string
    _start_year: string
    _end_year: string
  }>
  cv_url: string | null
  cover_letter_url: string | null
  diplomas_url: string | null
  work_certificates_url: string | null
  created_at: string | null
  modified_at: string | null
}

export default function MyApplicationsPage() {
  const navigate = useNavigate()
  const [applications, setApplications] = useState<Application[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    fetchApplications()
  }, [])

  const fetchApplications = async () => {
    try {
      setLoading(true)
      setError(null)

      const config = (window as any).WP_COCKPIT
      if (!config) throw new Error('WP Cockpit config not available')

      const url = `${config.restNonce ? '/wp-json' : ''}/kj-cockpit/v1/applications`
      const headers: Record<string, string> = {}
      if (config.restNonce) headers['X-WP-Nonce'] = config.restNonce

      const response = await fetch(url, {
        method: 'GET',
        credentials: 'same-origin',
        headers
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch applications')
      }

      setApplications(data.data || [])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load applications')
      console.error('Error fetching applications:', err)
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-center min-h-[400px]">
            <Spinner className="h-8 w-8" />
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">My Applications</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            Track your job applications and their status
          </p>
        </div>

      {applications.length === 0 ? (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-12">
              <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">No applications yet</h3>
              <p className="text-muted-foreground">
                You haven't submitted any job applications.
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {applications.map((app) => (
            <Card 
              key={app.id} 
              className="hover:shadow-lg transition-shadow cursor-pointer"
              onClick={() => navigate(`/applications/${app.id}`)}
            >
              <CardHeader>
                <CardTitle className="flex items-start justify-between">
                  <span className="text-lg">
                    {app.job_title || 'Job Application'}
                  </span>
                </CardTitle>
                <CardDescription className="space-y-1">
                  <div className="flex items-center gap-2 text-sm">
                    <Calendar className="h-3 w-3" />
                    <span>Applied: {formatDate(app.created_at)}</span>
                  </div>
                  {app.city && (
                    <div className="flex items-center gap-2 text-sm">
                      <MapPin className="h-3 w-3" />
                      <span>{app.city}{app.country && `, ${app.country}`}</span>
                    </div>
                  )}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Experience */}
                {app.experiences && app.experiences.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 text-sm font-medium mb-2">
                      <Briefcase className="h-4 w-4" />
                      <span>Experience</span>
                    </div>
                    <div className="space-y-1">
                      {app.experiences.slice(0, 2).map((exp, idx) => (
                        <div key={idx} className="text-xs text-muted-foreground">
                          {exp._role} at {exp._company}
                        </div>
                      ))}
                      {app.experiences.length > 2 && (
                        <div className="text-xs text-muted-foreground">
                          +{app.experiences.length - 2} more
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Education */}
                {app.education && app.education.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 text-sm font-medium mb-2">
                      <GraduationCap className="h-4 w-4" />
                      <span>Education</span>
                    </div>
                    <div className="space-y-1">
                      {app.education.slice(0, 2).map((edu, idx) => (
                        <div key={idx} className="text-xs text-muted-foreground">
                          {edu._diploma} - {edu._school}
                        </div>
                      ))}
                      {app.education.length > 2 && (
                        <div className="text-xs text-muted-foreground">
                          +{app.education.length - 2} more
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Skills */}
                {(app.technical_skills.length > 0 || app.tools.length > 0) && (
                  <div>
                    <div className="flex items-center gap-2 text-sm font-medium mb-2">
                      <Wrench className="h-4 w-4" />
                      <span>Skills & Tools</span>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {app.technical_skills.slice(0, 3).map((skill, idx) => (
                        <Badge key={`tech-${idx}`} variant="secondary" className="text-xs">
                          {skill}
                        </Badge>
                      ))}
                      {app.tools.slice(0, 2).map((tool, idx) => (
                        <Badge key={`tool-${idx}`} variant="outline" className="text-xs">
                          {tool}
                        </Badge>
                      ))}
                      {(app.technical_skills.length + app.tools.length > 5) && (
                        <Badge variant="secondary" className="text-xs">
                          +{app.technical_skills.length + app.tools.length - 5}
                        </Badge>
                      )}
                    </div>
                  </div>
                )}

                {/* Languages */}
                {app.languages.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 text-sm font-medium mb-2">
                      <Languages className="h-4 w-4" />
                      <span>Languages</span>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {app.languages.slice(0, 3).map((lang, idx) => (
                        <Badge key={idx} variant="secondary" className="text-xs">
                          {lang}
                        </Badge>
                      ))}
                      {app.languages.length > 3 && (
                        <Badge variant="secondary" className="text-xs">
                          +{app.languages.length - 3}
                        </Badge>
                      )}
                    </div>
                  </div>
                )}

                {/* Soft Skills */}
                {app.soft_skills.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 text-sm font-medium mb-2">
                      <Users className="h-4 w-4" />
                      <span>Soft Skills</span>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {app.soft_skills.slice(0, 3).map((skill, idx) => (
                        <Badge key={idx} variant="secondary" className="text-xs">
                          {skill}
                        </Badge>
                      ))}
                      {app.soft_skills.length > 3 && (
                        <Badge variant="secondary" className="text-xs">
                          +{app.soft_skills.length - 3}
                        </Badge>
                      )}
                    </div>
                  </div>
                )}

                {/* Documents */}
                <div className="pt-4 border-t space-y-2">
                  {app.cv_url && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full"
                      onClick={(e) => {
                        e.stopPropagation()
                        window.open(app.cv_url!, '_blank')
                      }}
                    >
                      <Download className="h-4 w-4 mr-2" />
                      CV
                    </Button>
                  )}
                  {app.cover_letter_url && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full"
                      onClick={(e) => {
                        e.stopPropagation()
                        window.open(app.cover_letter_url!, '_blank')
                      }}
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Cover Letter
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      </div>
    </div>
  )
}

